import React, { useContext } from "react";
import { TranslatorContext } from "../../context/Translator";
import { AnchorComponent } from "../../components/elements";
import LogoComponent from "../../components/LogoComponent";
import "./LoginPage.css";
import "./PrivacyPolicyPage.css";
import { APP_PREFIX_PATH } from "../../constant/constant";
import "bootstrap/dist/css/bootstrap.min.css";

/**
 * Public static privacy policy — same layout family as login (mc-auth).
 * Browser routes: /privacy-policy, {APP_PREFIX_PATH}/privacy-policy
 */
export default function PrivacyPolicyPage() {
    const { t } = useContext(TranslatorContext);

    return (
        <div className="mc-auth mc-privacy-auth">
            <img src="/2024/clinic/admin/images/logo.png" alt="" className="mc-auth-pattern" aria-hidden="true" />
            <div className="mc-auth-group mc-privacy-group">
                <article className="mc-privacy-card">
                    <LogoComponent
                        src="/2024/clinic/admin/images/logo.png"
                        alt="logo"
                        href="/"
                        className="mc-auth-logo d-block mx-auto mb-3"
                        style={{ width: "55px" }}
                    />

                    <header className="text-center mb-4">
                        <h1 className="h4 fw-semibold mb-1">{t("privacy")}</h1>
                        <p className="text-muted small mb-0">Last updated: May 17, 2026</p>
                    </header>

                    <div className="mc-privacy-content text-start small">
                        <section>
                            <h2>1. Introduction</h2>
                            <p>
                                Welcome to Waiz. This Privacy Policy explains how we collect, use, share, and protect
                                your personal information when you use our medical services platform, including our
                                website and mobile applications.
                            </p>
                            <p>
                                Waiz connects patients with healthcare providers. Administrators define medical
                                services and specialties (such as dentistry, cardiology, and dermatology). Clinics
                                register, manage their profiles, and maintain doctor records. Doctors work with clinics
                                and specialties and provide services through appointment and consultation bookings.
                            </p>
                            <p>
                                By using Waiz, you agree to this Privacy Policy. If you do not agree, please do not use
                                the app.
                            </p>
                        </section>

                        <section>
                            <h2>2. Information We Collect</h2>
                            <h3>2.1 Information you provide</h3>
                            <ul>
                                <li>
                                    Account and profile data: name, email address, phone number, password (stored in
                                    hashed form), profile photo, language preferences, and role-specific details.
                                </li>
                                <li>
                                    Clinic and doctor data: clinic name, address, contact details, doctor names,
                                    qualifications, specialties, availability, and records maintained by clinics for
                                    their doctors.
                                </li>
                                <li>
                                    Booking and service data: appointment or consultation type, date and time, selected
                                    service or specialty, notes you submit, payment-related references, and booking
                                    status.
                                </li>
                                <li>
                                    Communications: messages sent through contact forms, support requests, or in-app
                                    features.
                                </li>
                                <li>
                                    Account deletion requests: email, credentials used to verify identity, and optional
                                    reason for deletion when you use our account deletion page.
                                </li>
                            </ul>

                            <h3>2.2 Information collected automatically</h3>
                            <ul>
                                <li>
                                    Device and usage data: IP address, browser type, device identifiers, pages viewed,
                                    and actions taken on Waiz.
                                </li>
                                <li>
                                    Log and security data: authentication events, error logs, and fraud-prevention
                                    signals.
                                </li>
                                <li>
                                    Cookies and similar technologies where used to maintain sessions and improve
                                    performance.
                                </li>
                            </ul>

                            <h3>2.3 Sensitive and health-related information</h3>
                            <p>
                                If you provide health-related information (for example, symptoms, treatment notes, or
                                consultation details), we use it only to support bookings, care coordination, and app
                                operations. Please share only what is needed for your booking or care.
                            </p>
                        </section>

                        <section>
                            <h2>3. How We Use Your Information</h2>
                            <p>We use personal information to:</p>
                            <ul>
                                <li>Create and manage accounts for users, clinics, doctors, and administrators.</li>
                                <li>
                                    Enable clinics to register, define services under admin-configured specialties, and
                                    manage doctor records.
                                </li>
                                <li>
                                    Match users with clinics and doctors and process appointment and consultation
                                    bookings.
                                </li>
                                <li>Process payments, down payments, and related financial reporting where applicable.</li>
                                <li>Send transactional messages (confirmations, reminders, and account notices).</li>
                                <li>Provide customer support and respond to contact requests.</li>
                                <li>Improve, secure, and troubleshoot Waiz.</li>
                                <li>Comply with legal obligations and enforce our terms.</li>
                                <li>
                                    Send administrative or marketing communications where permitted and with your
                                    consent where required.
                                </li>
                            </ul>
                        </section>

                        <section>
                            <h2>4. How We Share Information</h2>
                            <p>We may share personal information with:</p>
                            <ul>
                                <li>
                                    <strong>Clinics and doctors</strong> — when you book a service, relevant booking and
                                    contact details are shared with the clinic and treating doctor to fulfill the
                                    appointment or consultation.
                                </li>
                                <li>
                                    <strong>Platform administrators</strong> — for oversight, support, reporting, and
                                    compliance with Waiz policies.
                                </li>
                                <li>
                                    <strong>Service providers</strong> — such as hosting, analytics, payment processors,
                                    and email delivery partners, under contracts that require appropriate safeguards.
                                </li>
                                <li>
                                    <strong>Legal and safety recipients</strong> — when required by law, court order, or
                                    to protect rights, safety, and security.
                                </li>
                                <li>
                                    <strong>Business transfers</strong> — in connection with a merger, acquisition, or
                                    sale of assets, subject to continued protection of your information.
                                </li>
                            </ul>
                            <p>We do not sell your personal information.</p>
                        </section>

                        <section>
                            <h2>5. Data Retention</h2>
                            <p>
                                We retain personal information for as long as your account is active or as needed to
                                provide Waiz, resolve disputes, enforce agreements, and meet legal and
                                regulatory requirements. Clinic and doctor records may be retained according to
                                applicable healthcare record-keeping rules and our internal policies. When you delete
                                your account, we will delete or anonymize your data within a reasonable period, except
                                where retention is required by law or for legitimate business purposes (such as fraud
                                prevention or financial records).
                            </p>
                        </section>

                        <section>
                            <h2>6. Security</h2>
                            <p>
                                We use reasonable measures to protect your information from unauthorized access, loss,
                                or misuse. No online system is completely secure, so please keep your login details safe.
                            </p>
                        </section>

                        <section>
                            <h2>7. Your Rights and Choices</h2>
                            <p>Depending on your location, you may have the right to:</p>
                            <ul>
                                <li>Access, correct, or update your personal information.</li>
                                <li>Request deletion of your account and associated data.</li>
                                <li>Object to or restrict certain processing.</li>
                                <li>Withdraw consent where processing is consent-based.</li>
                                <li>Request a copy of your data in a portable format.</li>
                            </ul>
                            <p>
                                You may delete your user account through our dedicated account deletion page at{" "}
                                <AnchorComponent to="/delete-user">/delete-user</AnchorComponent> (or the equivalent path
                                under your deployment prefix).
                            </p>
                            <p>
                                For other requests, contact us using the details in Section 10. We may verify your
                                identity before responding.
                            </p>
                        </section>

                        <section>
                            <h2>8. Children&apos;s Privacy</h2>
                            <p>
                                Waiz is not intended for children under 13. We do not knowingly collect personal
                                information from children without parental consent. If you believe a child has provided
                                data to us, please contact us so we can remove it.
                            </p>
                        </section>

                        <section>
                            <h2>9. International Users</h2>
                            <p>
                                If you use Waiz from another country, your information may be stored or processed where
                                our servers operate. Those locations may have different data protection laws.
                            </p>
                        </section>

                        <section>
                            <h2>10. Changes to This Policy</h2>
                            <p>
                                We may update this Privacy Policy from time to time. The latest version will always be
                                on this page with an updated date. Continued use of Waiz after changes means you accept
                                the updated policy.
                            </p>
                        </section>

                        <section>
                            <h2>11. Contact Us</h2>
                            <p>
                                If you have questions about this Privacy Policy, contact us through the contact options
                                in the app or via your clinic administrator.
                            </p>
                        </section>

                        <section>
                            <h2>12. Medical Disclaimer</h2>
                            <p>
                                Waiz is a technology platform that facilitates discovery, booking, and administration of
                                medical services. We do not provide medical advice, diagnosis, or treatment. Healthcare
                                services are delivered by independent clinics and licensed professionals. In a medical
                                emergency, contact local emergency services immediately.
                            </p>
                        </section>
                    </div>

                    <footer className="text-center mt-4 pt-3 border-top">
                        <AnchorComponent className="mc-auth-forgot" to={`${APP_PREFIX_PATH}/`}>
                            {t("sign_in")}
                        </AnchorComponent>
                    </footer>
                </article>
            </div>
        </div>
    );
}
